import React, {
  FunctionComponent,
  ReactNode,
  useState,
  useEffect,
  useContext,
} from 'react';
import { debounce } from 'lodash';
import { PatientContext } from '../../components/shared';
import { AffiliationSearch } from '../../interfaces/register';
import { AffiliationSearchField } from '../../components/register';
import makeSingle from '../../utils/makeSingle';
import searchAffiliationsAsync from '../../api/affiliations/searchAffiliationsAsync';
import getAffiliationRankingsAsync from '../../api/affiliations/getAffiliationRankingsAsync';
import getStateByZipAsync from '../../api/affiliations/getStateByZipAsync';
import {
  isCoverageCheckFlow,
  getCoverageData,
} from '../../lib/coverageCheckStore';

export interface AffiliationOptions {
  isPopular: boolean;
  affiliations: AffiliationSearch[] | null;
}

interface Props {
  errorMessage?: string;
  id: string;
  label?: string;
  onNoMatch(boolean): void;
  noMatchError?: ReactNode;
  placeholder?: string;
  pt?: string;
  toggleMemberIdForm(boolean): void;
  setShowSkipLink(boolean): void;
}

const AffiliationSearchFieldContainer: FunctionComponent<Props> = ({
  onNoMatch,
  setShowSkipLink,
  ...rest
}: Props) => {
  const [
    affiliationOptions,
    setAffiliationOptions,
  ] = useState<AffiliationOptions>({ isPopular: false, affiliations: null });
  const [popularAffiliationsByState, setPopularAffiliationsByState] = useState<
    { number: AffiliationSearch[] } | {}
  >({});
  const [showNoMatch, setShowNoMatch] = useState<boolean>(false);
  const [coverageStateId, setCoverageStateId] = useState<number | null>(null);
  const { patient } = useContext(PatientContext);

  // Authenticated patient's state wins; the guest coverage-check flow has no patient, so resolve a
  // state from the carried zip (it collects zip, not state) so the affiliation search can run.
  const stateId: number | null =
    (patient && patient.stateId) || coverageStateId;

  useEffect(() => {
    if (patient && patient.stateId) {
      return undefined;
    }
    if (!isCoverageCheckFlow()) {
      return undefined;
    }
    const { zip } = getCoverageData()?.values ?? {};
    if (!zip) {
      return undefined;
    }
    let active = true;
    getStateByZipAsync(zip).then(({ stateId: resolved }) => {
      if (active && resolved) {
        setCoverageStateId(resolved);
      }
    });
    return () => {
      active = false;
    };
  }, [patient && patient.stateId]);

  const getPopularAffiliations = async (): Promise<void> => {
    const popular = popularAffiliationsByState;

    if (stateId) {
      if (typeof popular[stateId] === 'undefined') {
        const { affiliations } = await getAffiliationRankingsAsync(stateId);
        popular[stateId] = affiliations;
        setPopularAffiliationsByState(popular);
      }
      setAffiliationOptions({
        isPopular: true,
        affiliations: popular[stateId],
      });
    }
  };

  useEffect(() => {
    getPopularAffiliations();
  }, [stateId]);

  const singleAffiliationChange = makeSingle(function* singleAffiliationChange(
    value: string
  ) {
    const { affiliations } =
      value.length && stateId
        ? yield searchAffiliationsAsync(value, stateId)
        : { affiliations: [] };

    if (!value && affiliations.length === 0) {
      getPopularAffiliations();
      setShowSkipLink(true);
    } else {
      setAffiliationOptions({ isPopular: false, affiliations });
    }
    setShowNoMatch(affiliations.length === 0);
    onNoMatch(affiliations.length === 0);
  });

  const handleAffiliationChange = debounce(singleAffiliationChange, 250);

  return (
    <AffiliationSearchField
      affiliationOptions={affiliationOptions}
      stateId={stateId}
      onInputValueChange={handleAffiliationChange}
      showNoMatch={showNoMatch}
      setShowSkipLink={setShowSkipLink}
      {...rest}
    />
  );
};

export default AffiliationSearchFieldContainer;
